/**
 * One-shot "this browser just completed X" markers, for conversions that can
 * only be confirmed by a server action which then REDIRECTS.
 *
 * The problem this solves: an Ads conversion has to fire client-side, but the
 * only code that knows the conversion happened runs on the server and answers
 * with a 302. A cookie is the one channel that survives that redirect and is
 * readable by the page the visitor lands on.
 *
 * Why not the URL: a `?converted=1` param — or a dedicated success path used on
 * its own — survives sharing, bookmarking and back-navigation, every one of
 * which would re-fire the conversion. `fireAdsConversion`'s sessionStorage
 * dedupe does not save you there, because a NEW tab starts with empty
 * sessionStorage.
 *
 * The cookie is deliberately NOT httpOnly: the client deletes it the moment the
 * conversion fires, and that is what makes it fire exactly once. It is a
 * boolean marker with no secret in it, and forging one buys an attacker nothing
 * they could not get by calling gtag from the console.
 *
 * Extracted from the signup implementation so the enquiry conversion could
 * reuse it rather than re-derive it. The `Secure`-attribute rule below is
 * subtle enough that having two copies of it was the actual risk.
 */

// Long enough to survive the redirect and a slow first paint, short enough that
// an abandoned tab cannot fire a conversion an hour later.
const MAX_AGE_SECONDS = 300

/**
 * `Set-Cookie` value to append to a redirect response (server side).
 *
 * `value` is an opaque per-conversion id when the caller has one. It becomes
 * the dedupe key, which is what lets the SAME visitor convert twice in one
 * session — someone enquiring about a second site is a second lead, and a
 * constant key would silently drop it. Callers with nothing to identify (a
 * signup is once per browser by definition) leave it at the default.
 */
export function conversionMarkerCookie(name: string, value = '1'): string {
	const secure = process.env.NODE_ENV === 'production' ? '; Secure' : ''
	return `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`
}

/** True when this browser just completed the marked action (client side). */
export function hasConversionMarker(name: string): boolean {
	return readConversionMarker(name) !== null
}

/**
 * The marker's value, or null when absent (client side).
 *
 * Returns `'1'` for a valueless marker, so `null` always means "no conversion"
 * and never "conversion with an empty id".
 */
export function readConversionMarker(name: string): string | null {
	if (typeof document === 'undefined') return null
	return readMarkerFrom(document.cookie, name)
}

/**
 * Same read, against a `Cookie` request header (server side).
 *
 * A loader is the only place that can read the marker without racing the
 * client-side clear: it runs before the page renders, whereas the effect that
 * fires the Ads conversion may have already deleted the cookie by the time a
 * component effect looks for it.
 */
export function readConversionMarkerFromRequest(
	request: Request,
	name: string,
): string | null {
	return readMarkerFrom(request.headers.get('cookie') ?? '', name)
}

function readMarkerFrom(cookieHeader: string, name: string): string | null {
	const prefix = `${name}=`
	for (const part of cookieHeader.split(';')) {
		const trimmed = part.trim()
		if (!trimmed.startsWith(prefix)) continue
		const raw = trimmed.slice(prefix.length)
		if (!raw) return null
		try {
			return decodeURIComponent(raw)
		} catch {
			// A hand-forged cookie can contain a malformed escape. Treat the
			// marker as present but unidentifiable rather than throwing in a
			// loader.
			return raw
		}
	}
	return null
}

/**
 * Removes the marker. Call only once the conversion actually fired.
 *
 * The attributes here must MATCH the ones used to set it. A browser will not
 * let a non-Secure write overwrite a Secure cookie, so omitting `Secure` in
 * production silently fails to delete: the marker then survives its full
 * lifetime, and although the same tab is protected by the sessionStorage
 * dedupe, a NEW tab starts with empty sessionStorage, still sees the cookie,
 * and fires a duplicate conversion. Keyed off the protocol rather than
 * NODE_ENV because that is the actual condition the browser enforces.
 */
export function clearConversionMarker(name: string): void {
	if (typeof document === 'undefined') return
	const secure = window.location.protocol === 'https:' ? '; Secure' : ''
	document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`
}
